import type { ServiceContainer } from "./ServiceContainer";
import type { TypesMap } from "./TypesMap";

/**
 * Base ServiceContainer interface implementation.
 */
export class BaseServiceContainer<M extends TypesMap = TypesMap>
  implements ServiceContainer<M>
{
  /**
   * Parent container.
   *
   * @protected
   */
  protected parent: BaseServiceContainer<M> | null = null;

  /**
   * Services storage.
   *
   * @protected
   */
  protected services: Map<keyof M, unknown>;

  /**
   * Backups storage.
   *
   * @private
   */
  private backups: Map<keyof M, unknown>[];

  /**
   * BaseServiceContainer constructor.
   */
  constructor() {
    this.services = new Map();
    this.backups = [];
  }

  /**
   * @inheritDoc
   */
  register<K extends keyof M>(
    key: K,
    service: M[K] | ((locator: ServiceContainer<M>) => M[K])
  ) {
    this.services.set(key, service);
  }

  /**
   * @inheritDoc
   */
  unregister<K extends keyof M>(key: K) {
    this.services.delete(key);
  }

  /**
   * @inheritDoc
   */
  resolve<K extends keyof M>(key: K): M[K] {
    const service = this.services.get(key);
    if (service === undefined) {
      if (this.parent) {
        return this.parent.resolve(key);
      }
      throw new RangeError(
        `Service "${key as string}" was not found in container.`
      );
    }
    if (typeof service === "function") {
      return service(this);
    }
    return service as M[K];
  }

  /**
   * @inheritDoc
   */
  backup() {
    const backup = new Map(this.services);
    this.backups.push(backup);
  }

  /**
   * @inheritDoc
   */
  restore() {
    const backup = this.backups.pop();
    if (backup) {
      this.services.clear();
      this.services = backup;
    }
  }

  /**
   * @inheritDoc
   */
  createChild(): BaseServiceContainer<M> {
    const child = new BaseServiceContainer<M>();
    child.parent = this;
    return child;
  }
}
